import React, { useState, useEffect } from 'react';
import withAuth from '../common/auth';
import axios from 'axios';
import { BASE_URL, GET_SUPPLIER_MASTER, GET_SUPPLIER_DUEBILLS, POST_SUPPLIER_PAYMENT } from '../utlilities/config';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import Select from 'react-select';

// Icons
import { 
  FiSearch, 
  FiRefreshCw, 
  FiAlertCircle, 
  FiCheckCircle, 
  FiCalendar,
  FiFileText,
  FiClock
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import { IoWalletOutline } from "react-icons/io5";
import { CiCreditCard2 } from "react-icons/ci";
import { TbTruckDelivery } from "react-icons/tb";

// Animated loader component
const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading payment data...</p>
    </div>
  </div>
);

// No Data component
const NoData = () => (
  <div className="flex flex-col items-center justify-center h-64 py-10">
    <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
      <FiFileText size={32} />
    </div>
    <h3 className="text-lg font-medium text-gray-700">No bills found</h3>
    <p className="text-sm text-gray-500 mt-1 mb-6">Please select a supplier to view pending bills</p>
  </div>
);

// Custom select styles
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#ffffff',
    borderColor: state.isFocused ? '#4f46e5' : '#e5e7eb',
    borderRadius: '0.5rem',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(79, 70, 229, 0.2)' : 'none',
    padding: '2px',
    '&:hover': {
      borderColor: '#4f46e5',
    },
    transition: 'all 0.2s',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected 
      ? 'rgba(79, 70, 229, 0.1)' 
      : state.isFocused 
        ? 'rgba(79, 70, 229, 0.05)' 
        : '#ffffff',
    color: state.isSelected ? '#4f46e5' : '#374151',
    '&:hover': {
      backgroundColor: 'rgba(79, 70, 229, 0.05)',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    borderRadius: '0.5rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  }),
  menuPortal: base => ({ ...base, zIndex: 9999 }),
};

function PaymentSupplier() {
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('accessToken');
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [dueBills, setDueBills] = useState([]);
  const [checkedItems, setCheckedItems] = useState([]);
  const [selectedButton, setSelectedButton] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [payingAmount, setPayingAmount] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [supplierHistory, setSupplierHistory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoverRow, setHoverRow] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  const getSupplierCreditDays = (supplier) => {
    if (!supplier) return 0;
    const creditDays = Number(
      supplier.CreditDays ??
      supplier.creditDays ??
      supplier.credit_days ??
      0
    );
    return Number.isFinite(creditDays) ? creditDays : 0;
  };

  const getBillDueDate = (bill, supplier) => {
    const invoiceDateValue = bill?.InvoiceDate;
    if (!invoiceDateValue) return null;
    const invoiceDateObj = new Date(invoiceDateValue);
    if (Number.isNaN(invoiceDateObj.getTime())) return null;
    const creditDays = getSupplierCreditDays(supplier);
    invoiceDateObj.setDate(invoiceDateObj.getDate() + creditDays);
    return invoiceDateObj;
  };

  const getDueStatusMeta = (bill, supplier) => {
    const dueDate = getBillDueDate(bill, supplier);
    if (!dueDate) {
      return {
        label: 'Due',
        badgeClass: 'bg-gray-100 text-gray-700',
        dotClass: 'bg-gray-500',
      };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueOnly = new Date(dueDate);
    dueOnly.setHours(0, 0, 0, 0);

    const diffInDays = Math.floor((dueOnly.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays < 0) {
      const overdueDays = Math.abs(diffInDays);
      return {
        label: `Due for ${overdueDays} day${overdueDays === 1 ? '' : 's'}`,
        badgeClass: 'bg-red-100 text-red-800',
        dotClass: 'bg-red-500',
      };
    }

    if (diffInDays === 0) {
      return {
        label: 'Due today',
        badgeClass: 'bg-amber-100 text-amber-800',
        dotClass: 'bg-amber-500',
      };
    }

    return {
      label: `Due in ${diffInDays} day${diffInDays === 1 ? '' : 's'}`,
      badgeClass: 'bg-green-100 text-green-800',
      dotClass: 'bg-green-500',
    };
  };

  useEffect(() => {
    fetchSuppliers();
    // Prefill the date with the current date in local timezone (IST)
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const today = `${year}-${month}-${day}`;
    setCurrentDate(today);
  }, []);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_SUPPLIER_MASTER}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });
      if (response.status === 200) {
        setSuppliers(response.data.data || []);
      }
    } catch (error) {
      toast.error('Failed to load suppliers', {
        autoClose: 1000,
        icon: <FiAlertCircle className="text-red-500" />
      });
      console.error('Error fetching suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierChange = async (selectedOption) => {
    setSelectedSupplier(selectedOption);
    setCheckedItems([]);

    if (!selectedOption) {
      setDueBills([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_SUPPLIER_DUEBILLS}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'supplierID': selectedOption.value,
        },
      });
      if (response.status === 200) {
        setDueBills(response.data.data || []);
        
        // Mock history data - replace with actual API call in production
        setSupplierHistory({
          lastPaymentDate: '29/06/2024',
          lastPaymentAmount: 12000
        });
      }
    } catch (error) {
      toast.error('Failed to load supplier bills', {
        autoClose: 1000,
        icon: <FiAlertCircle className="text-red-500" />
      });
      console.error('Error fetching supplier bills:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxClick = (billRefNo) => {
    setCheckedItems((prevCheckedItems) =>
      prevCheckedItems.includes(billRefNo)
        ? prevCheckedItems.filter((item) => item !== billRefNo)
        : [...prevCheckedItems, billRefNo]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allBillRefNos = dueBills.map(bill => bill.PurchaseRefNo);
      setCheckedItems(allBillRefNos);
    } else {
      setCheckedItems([]);
    }
  };

  const handlePaymentMethodClick = (buttonType) => {
    setSelectedButton(buttonType);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const getCheckedBillsTotal = () => {
    return dueBills
      .filter((item) => checkedItems.includes(item.PurchaseRefNo))
      .reduce((acc, item) => acc + (parseFloat(item.BalanceDue) || 0), 0)
      .toFixed(2);
  };

  const getCheckedBillsLabels = () => {
    const bills = dueBills
      .filter((item) => checkedItems.includes(item.PurchaseRefNo))
      .map((item) => item.PurchaseRefNo);
    
    if (bills.length === 0) return '';
    if (bills.length === 1) return bills[0];
    return `${bills[0]} +${bills.length - 1} more`;
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return null;
    }
    return sortConfig.direction === 'ascending' ? '↑' : '↓';
  };

  const filteredAndSortedBills = React.useMemo(() => {
    // Filter bills based on search query
    let filteredBills = dueBills;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filteredBills = dueBills.filter(bill => 
        (bill.PurchaseRefNo?.toLowerCase() || '').includes(query) ||
        (bill.TotalAmount?.toString() || '').includes(query) ||
        (bill.BalanceDue?.toString() || '').includes(query)
      );
    }

    // Sort filtered bills
    if (sortConfig.key) {
      filteredBills = [...filteredBills].sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        
        // For dates
        if (sortConfig.key === 'InvoiceDate') {
          const aDate = new Date(aValue);
          const bDate = new Date(bValue);
          return sortConfig.direction === 'ascending' 
            ? aDate - bDate 
            : bDate - aDate;
        }

        // For computed due date (InvoiceDate + supplier credit days)
        if (sortConfig.key === 'DueDate') {
          const aDue = getBillDueDate(a, selectedSupplier);
          const bDue = getBillDueDate(b, selectedSupplier);
          const aTime = aDue ? aDue.getTime() : 0;
          const bTime = bDue ? bDue.getTime() : 0;
          return sortConfig.direction === 'ascending'
            ? aTime - bTime
            : bTime - aTime;
        }
        
        // For numeric values
        if (['TotalAmount', 'BalanceDue'].includes(sortConfig.key)) {
          return sortConfig.direction === 'ascending' 
            ? parseFloat(aValue) - parseFloat(bValue) 
            : parseFloat(bValue) - parseFloat(aValue);
        }
        
        // For strings
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filteredBills;
  }, [dueBills, searchQuery, sortConfig, selectedSupplier]);

  const handleSubmit = async () => {
    if (!selectedSupplier) {
      toast.error('Please select a supplier', {
        autoClose: 1000,
        icon: <FiAlertCircle className="text-red-500" />
      });
      return;
    }

    if (checkedItems.length === 0) {
      toast.error('Please select at least one bill', {
        autoClose: 1000,
        icon: <FiAlertCircle className="text-red-500" />
      });
      return;
    }

    if (!payingAmount || parseFloat(payingAmount) <= 0) {
      toast.error('Please enter a valid payment amount', {
        autoClose: 1000,
        icon: <FiAlertCircle className="text-red-500" />
      });
      return;
    }

    if (!selectedButton) {
      toast.error('Please select a payment method', {
        autoClose: 1000,
        icon: <FiAlertCircle className="text-red-500" />
      });
      return;
    }

    setLoading(true);
    try {
      let remainingAmount = parseFloat(payingAmount);
      const updatedBills = dueBills
        .filter((item) => checkedItems.includes(item.PurchaseRefNo))
        .map((bill, index) => {
          const { TotalAmount, Sequence, InvoiceNo, EditNo, InvoiceDate } = bill;
          let PaidAmount = 0;
  
          if (remainingAmount >= TotalAmount) {
            PaidAmount = TotalAmount;
            remainingAmount -= PaidAmount;
          } else if (remainingAmount > 0) {
            PaidAmount = remainingAmount;
            remainingAmount = 0;
          }
  
          return {
            SINo: index + 1,
            InvoiceSequence: Sequence,
            InvoiceNo: InvoiceNo,
            InvoiceEditNo: EditNo,
            InvoiceDate: InvoiceDate.split('T')[0],
            TotalAmount: TotalAmount,
            PaidAmount: PaidAmount
          };
        });
  
      const payload = {
        SupplierID: selectedSupplier.value,
        SupplierName: selectedSupplier.label,
        PaymentDate: currentDate,
        CashAmount: selectedButton === 'cash' ? parseFloat(payingAmount) : 0,
        BankAmount: selectedButton === 'bank' ? parseFloat(payingAmount) : 0,
        Remarks: remarks,
        InvoiceDetails: updatedBills
      };

      const response = await axios.post(`${BASE_URL}${POST_SUPPLIER_PAYMENT}`, payload, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
  
      if (response.status === 200) {
        toast.success('Payment processed successfully', {
          autoClose: 1000,
          icon: <FiCheckCircle className="text-green-500" />
        });
        // Reset form and refresh data
        resetForm();
        fetchSuppliers();
      }
    } catch (error) {
      console.error('Payment failed:', error.response?.data || error.message);
      toast.error('Payment failed. Please try again.', {
        autoClose: 1000,
        icon: <FiAlertCircle className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedSupplier(null);
    setDueBills([]);
    setCheckedItems([]);
    setSelectedButton(null);
    setRemarks('');
    setPayingAmount('');
    setSearchQuery('');
    setSupplierHistory(null);
  };

  const totalDueAmount = dueBills.reduce(
    (sum, bill) => sum + parseFloat(bill.BalanceDue || 0),
    0
  );

  const dueThisMonthAmount = dueBills.reduce((sum, bill) => {
    const computedDueDate = getBillDueDate(bill, selectedSupplier);
    if (!computedDueDate) return sum;
    const today = new Date();
    const isSameMonth =
      computedDueDate.getFullYear() === today.getFullYear() &&
      computedDueDate.getMonth() === today.getMonth();
    if (!isSameMonth) return sum;
    return sum + parseFloat(bill.BalanceDue || 0);
  }, 0);

  return (
    <div className="flex h-full bg-background">
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={3}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      
      {loading && <Spinner />}

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Page Header */}
        <div className="bg-white p-3 mb-3">
          <div className="flex justify-between items-center">
            <h1 className="text-lg font-bold text-black flex items-center">
              Supplier Payment
            </h1>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handlePaymentMethodClick('cash')}
                className={`py-1 px-3 rounded-md flex items-center text-sm transition-all duration-200 ${
                  selectedButton === 'cash'
                    ? 'bg-indigo-50 text-indigo-600 border border-indigo-200'
                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                <IoWalletOutline className="mr-1.5" size={16} />
                Cash Payment
              </button>

              <button
                type="button"
                onClick={() => handlePaymentMethodClick('bank')}
                className={`py-1 px-3 rounded-md flex items-center text-sm transition-all duration-200 ${
                  selectedButton === 'bank'
                    ? 'bg-indigo-50 text-indigo-600 border border-indigo-200'
                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                <CiCreditCard2 className="mr-1.5" size={16} />
                Bank Transfer
              </button>

              <button
                onClick={handleSubmit}
                className="py-1 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-sm rounded-md shadow-sm hover:shadow transition-all"
              >
                Process Payment
              </button>

              <button
                className="py-1 px-4 bg-indigo-600 text-white font-medium text-sm rounded-md hover:bg-indigo-700 shadow-sm transition-all flex items-center"
                onClick={resetForm}
              >
                <FiRefreshCw className="mr-2" /> Reset Form
              </button>
            </div>
          </div>
        </div>
        
        {/* Main Content Grid */}
        <div className="grid grid-cols-3 gap-6 mb-3 p-3">
          {/* Payment Form */}
          <div className="col-span-2 bg-white rounded-xl shadow-md p-6 flex flex-col"  style={{ minHeight: '365px' }}>
            <h2 className="text-lg font-medium text-gray-800 mb-4">Payment Details</h2>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Supplier</label>
                <Select
                  options={suppliers.map(supplier => ({
                    value: supplier.SupplierID,
                    label: supplier.SupplierName,
                    CreditDays: supplier.CreditDays ?? 0
                  }))}
                  value={selectedSupplier}
                  onChange={handleSupplierChange}
                  placeholder="Choose a supplier"
                  isSearchable
                  styles={customSelectStyles}
                  menuPortalTarget={document.body}
                  className="w-full"
                  isClearable
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                    <FiCalendar size={16} />
                  </div>
                  <input
                    type="date"
                    value={currentDate}
                    onChange={(e) => setCurrentDate(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Selected Bills</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                    <FiFileText size={16} />
                  </div>
                  <input
                    type="text"
                    value={getCheckedBillsLabels()}
                    readOnly
                    placeholder="No bills selected"
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Due Amount</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                    <FaRupeeSign size={16} />
                  </div>
                  <input
                    type="text"
                    value={getCheckedBillsTotal()}
                    readOnly
                    placeholder="0.00"
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none"
                  />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <input
                  type="text"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Add payment notes (optional)"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                    <FaRupeeSign size={16} />
                  </div>
                  <input
                    type="number"
                    value={payingAmount}
                    onChange={(e) => setPayingAmount(e.target.value)}
                    placeholder="Enter amount to pay"
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
            
          </div>
          
          {/* Supplier History Card */}
          <div className="col-span-1 bg-white rounded-xl shadow-md p-6" style={{ minHeight: '365px' }}>
            <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
              <FiClock className="mr-2 text-indigo-500" /> Payment History
            </h2>
            
            {selectedSupplier && supplierHistory ? (
              <div className="space-y-6">
                
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-800">Payment Summary</h3>
                  
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Total Pending</span>
                    <span className="text-sm font-medium text-gray-800">{dueBills.length}</span>
                  </div>
                  
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Due This Month</span>
                    <span className="text-sm font-medium text-red-600">₹{dueThisMonthAmount.toLocaleString()}</span>
                  </div>

                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Total Due</span>
                    <span className="text-sm font-medium text-gray-800">₹{totalDueAmount.toLocaleString()}</span>
                  </div>
                  
                  <div className="flex justify-between py-2">
                    <span className="text-sm text-gray-600">Selected</span>
                    <span className="text-sm font-medium text-indigo-600">₹{getCheckedBillsTotal()}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64">
                <div className="w-16 h-16 mb-4 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
                  <FiFileText size={24} />
                </div>
                <p className="text-sm text-gray-500 text-center">
                  Select a supplier to view payment history
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Bills Table Section */}
        <div className="bg-white rounded-xl shadow-md p-3 flex-1 flex flex-col overflow-hidden ml-3 mr-3 mb-3">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-medium text-gray-800">Pending Bills</h2>
            
            {dueBills.length > 0 && (
              <div className="relative w-64">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                  <FiSearch size={16} />
                </div>
                <input
                  type="text"
                  placeholder="Search bills..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            )}
          </div>
          
          {selectedSupplier ? (
            filteredAndSortedBills.length > 0 ? (
              <div className="overflow-auto flex-1 min-h-[200px]">
                <table className="w-full border-collapse">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3 text-left border-b">
                        <input 
                          type="checkbox" 
                          checked={checkedItems.length > 0 && checkedItems.length === filteredAndSortedBills.length}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => requestSort('PurchaseRefNo')}
                      >
                        <div className="flex items-center">
                          <span>Bill Number</span>
                          <span className="ml-1">{getSortIcon('PurchaseRefNo')}</span>
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => requestSort('InvoiceDate')}
                      >
                        <div className="flex items-center">
                          <span>Invoice Date</span>
                          <span className="ml-1">{getSortIcon('InvoiceDate')}</span>
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => requestSort('DueDate')}
                      >
                        <div className="flex items-center">
                          <span>Due Date</span>
                          <span className="ml-1">{getSortIcon('DueDate')}</span>
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => requestSort('TotalAmount')}
                      >
                        <div className="flex items-center">
                          <span>Total Amount</span>
                          <span className="ml-1">{getSortIcon('TotalAmount')}</span>
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => requestSort('BalanceDue')}
                      >
                        <div className="flex items-center">
                          <span>Due Amount</span>
                          <span className="ml-1">{getSortIcon('BalanceDue')}</span>
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Status
                      </th>
                    </tr>
                  </thead>
                  
                  <tbody className="divide-y divide-gray-100">
                    {filteredAndSortedBills.map((bill, index) => (
                      <tr 
                        key={index} 
                        className={`${
                          hoverRow === index ? 'bg-indigo-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        } transition-colors hover:bg-indigo-50`}
                        onMouseEnter={() => setHoverRow(index)}
                        onMouseLeave={() => setHoverRow(null)}
                      >
                        <td className="px-6 py-4">
                          <input 
                            type="checkbox" 
                            checked={checkedItems.includes(bill.PurchaseRefNo)}
                            onChange={() => handleCheckboxClick(bill.PurchaseRefNo)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-indigo-600">
                          {bill?.PurchaseRefNo}
                        </td>
                                                  <td className="px-6 py-4 text-sm text-gray-600">
                          {bill?.InvoiceDate ? new Date(bill.InvoiceDate).toLocaleDateString('en-IN') : ''}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {(() => {
                            const computedDueDate = getBillDueDate(bill, selectedSupplier);
                            return computedDueDate ? computedDueDate.toLocaleDateString('en-IN') : '';
                          })()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          ₹{parseFloat(bill?.TotalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-red-600">
                          ₹{parseFloat(bill?.BalanceDue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4">
                          {(() => {
                            const statusMeta = getDueStatusMeta(bill, selectedSupplier);
                            return (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusMeta.badgeClass}`}>
                                <span className={`w-1.5 h-1.5 mr-1.5 rounded-full ${statusMeta.dotClass}`}></span>
                                {statusMeta.label}
                              </span>
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <NoData />
              </div>
            )
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center justify-center py-10">
                <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
                  <TbTruckDelivery size={32} />
                </div>
                <h3 className="text-lg font-medium text-gray-700">No supplier selected</h3>
                <p className="text-sm text-gray-500 mt-1 mb-6">Select a supplier to view pending bills</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default withAuth(PaymentSupplier);